import React, { Fragment, memo, useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
} from "@mui/material";
import { ExpandLess, ExpandMore, LogoutTwoTone } from "@mui/icons-material";

import colors from "app/styles/colors";
import useAuth from "app/hooks/useAuth";
import Images from "app/assets/Images";
import Navigation from "app/Navigation";

const drawerWidth = 270;

function SideNav() {
  const { pathname } = useLocation();
  const { role, logout } = useAuth();

  const [expand, setExpand] = useState([]);

  // ===============================
  // Collapse handler
  // ===============================
  const handleCollapse = (label) => {
    setExpand((prev) =>
      prev.includes(label) ? prev.filter((i) => i !== label) : [...prev, label]
    );
  };

  // ===============================
  // Active checker (BOOLEAN ONLY)
  // ===============================
  const isSubRouteActive = useCallback(
    (item) => {
      if (!item) return false;
      return item.childPath?.includes(pathname);
    },
    [pathname]
  );

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          bgcolor: colors.white,
          overflowY: "auto",
        },
      }}
    >
      {/* Logo */}
      <Box sx={{ display: "flex", p: 1, justifyContent: "center", mb: 1 }}>
        <img src={Images.logo} alt="Logo" width="50%" />
      </Box>

      <List sx={{ px: 2 }}>
        {Navigation.map((item) => {
          if (!(role === item.type || item.type === "")) return null;

          const isItemActive = item.path === pathname || isSubRouteActive(item);

          const isExpanded = expand.includes(item.label);

          return (
            <Fragment key={item.label}>
              {/* ================= Parent ================= */}
              <ListItemButton
                component={item.path ? Link : "div"}
                to={item.path || ""}
                onClick={() => item.childPath && handleCollapse(item.label)}
                sx={{
                  borderRadius: 1,
                  mt: 1.5,
                  bgcolor: isItemActive ? colors.primaryLight : "",
                  "&:hover": {
                    bgcolor: colors.primaryLight,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isItemActive ? colors.primary : colors.textSecondary,
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: isItemActive ? "bold" : "normal",
                        color: isItemActive
                          ? colors.primary
                          : colors.textSecondary,
                      }}
                    >
                      {item.label}
                    </Typography>
                  }
                />

                {item.children &&
                  (isExpanded ? (
                    <ExpandLess sx={{ color: colors.textSecondary }} />
                  ) : (
                    <ExpandMore sx={{ color: colors.textSecondary }} />
                  ))}
              </ListItemButton>

              {/* ================= Children ================= */}
              {item.children && (
                <Collapse in={isExpanded} timeout="auto">
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const isChildActive =
                        child.path === pathname || isSubRouteActive(child);

                      const isChildExpanded = expand.includes(child.label);

                      return (
                        <Fragment key={child.label}>
                          <ListItemButton
                            component={child.path ? Link : "div"}
                            to={child.path || ""}
                            onClick={() =>
                              child.childPath && handleCollapse(child.label)
                            }
                            sx={{
                              pl: 4,
                              mt: 1,
                              borderRadius: 1,
                              "&:hover": {
                                color: colors.primary,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isChildActive
                                  ? colors.primary
                                  : colors.textSecondary,
                                minWidth: 30,
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>

                            <ListItemText
                              primary={
                                <Typography
                                  sx={{
                                    fontSize: 13,
                                    fontWeight: isChildActive
                                      ? "bold"
                                      : "normal",
                                    color: isChildActive
                                      ? colors.primary
                                      : colors.textSecondary,
                                  }}
                                >
                                  {child.label}
                                </Typography>
                              }
                            />

                            {child.children &&
                              (isChildExpanded ? (
                                <ExpandLess
                                  sx={{ color: colors.textSecondary }}
                                />
                              ) : (
                                <ExpandMore
                                  sx={{ color: colors.textSecondary }}
                                />
                              ))}
                          </ListItemButton>

                          {/* ================= Sub-Children ================= */}
                          {child.children && (
                            <Collapse in={isChildExpanded} timeout="auto">
                              <List
                                component="div"
                                disablePadding
                                sx={{
                                  ml: 2,
                                  bgcolor: colors.whiteSmoke,
                                  borderRadius: 2,
                                }}
                              >
                                {child.children.map((subChild) => {
                                  const isSubActive =
                                    subChild.path === pathname;

                                  return (
                                    <ListItemButton
                                      key={subChild.path}
                                      component={Link}
                                      to={subChild.path}
                                      sx={{
                                        pl: 4,
                                        mt: 1,
                                        borderRadius: 1,
                                        color: isSubActive
                                          ? colors.primary
                                          : colors.textSecondary,
                                      }}
                                    >
                                      <ListItemIcon
                                        sx={{
                                          color: isSubActive
                                            ? colors.primary
                                            : colors.textSecondary,
                                          minWidth: 30,
                                        }}
                                      >
                                        {subChild.icon}
                                      </ListItemIcon>

                                      <ListItemText
                                        primary={
                                          <Typography
                                            sx={{
                                              fontSize: 13,
                                              fontWeight: isSubActive
                                                ? "bold"
                                                : "normal",
                                            }}
                                          >
                                            {subChild.label}
                                          </Typography>
                                        }
                                      />
                                    </ListItemButton>
                                  );
                                })}
                              </List>
                            </Collapse>
                          )}
                        </Fragment>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Fragment>
          );
        })}

        {/* ================= Logout ================= */}
        <ListItemButton
          onClick={logout}
          sx={{
            borderRadius: 1,
            mt: 1.5,
            bgcolor: colors.white,
            "&:hover": {
              bgcolor: colors.primaryLight,
              color: colors.primary,
              ".MuiTypography-root": {
                color: colors.primary,
              },
              ".MuiListItemIcon-root": {
                color: colors.primary,
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: colors.textSecondary,
              minWidth: 40,
              fontSize: "20px",
            }}
          >
            <LogoutTwoTone />
          </ListItemIcon>

          <ListItemText
            primary={
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: "normal",
                  color: colors.textSecondary,
                }}
              >
                Logout
              </Typography>
            }
          />
        </ListItemButton>
      </List>
    </Drawer>
  );
}

export default memo(SideNav);
